/**
 * Submission Consistency Card - Phase 2 Component
 * Tracks on-time submission patterns with visual timeline
 * Location: Performance Metrics Section (5th card)
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { format, startOfWeek, addDays, differenceInDays, subWeeks } from 'date-fns';
import { getDeadlineForWeek, isSubmissionOnTime } from '@/services/timesheetSettingsService';

interface WeekSubmission {
  weekEnding: Date;
  status: 'on-time' | 'late' | 'missing' | 'current';
  daysLate?: number;
  submitted: boolean;
}

export function SubmissionConsistencyCard() {
  const { timesheets, currentEmployee } = useTimesheetForScreen('homepage');
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummary(currentEmployee.id, 'homepage');
  const mode = getScreenMode('homepage');
  const navigate = useNavigate();

  const consistencyData = useMemo(() => {
    const today = new Date();
    const currentWeek = startOfWeek(today, { weekStartsOn: 1 });
    
    // Build the visual timeline from client-side timesheet data
    // (the backend doesn't return per-week breakdown, only aggregates)
    const weeks: WeekSubmission[] = [];
    
    for (let i = 0; i < 12; i++) {
      const weekStart = subWeeks(currentWeek, i);
      const weekEnd = addDays(weekStart, 6);
      const submissionDeadline = getDeadlineForWeek(weekStart);
      const isCurrentWeek = format(weekEnd, 'yyyy-MM-dd') === format(addDays(currentWeek, 6), 'yyyy-MM-dd');
      
      const timesheet = timesheets.find(
        ts => ts.employeeId === currentEmployee.id &&
        format(ts.weekEnding, 'yyyy-MM-dd') === format(weekEnd, 'yyyy-MM-dd')
      );

      if (isCurrentWeek) {
        weeks.push({
          weekEnding: weekEnd,
          status: 'current',
          submitted: !!timesheet && timesheet.status !== 'draft'
        });
      } else if (!timesheet || timesheet.status === 'draft') {
        weeks.push({
          weekEnding: weekEnd,
          status: 'missing',
          submitted: false
        });
      } else if (timesheet.submittedAt) {
        const submittedDate = new Date(timesheet.submittedAt);
        const onTime = isSubmissionOnTime(submittedDate, weekStart);
        const daysLate = differenceInDays(submittedDate, submissionDeadline);
        
        weeks.push({
          weekEnding: weekEnd,
          status: onTime ? 'on-time' : 'late',
          daysLate: Math.max(0, daysLate),
          submitted: true
        });
      } else {
        weeks.push({
          weekEnding: weekEnd,
          status: 'missing',
          submitted: false
        });
      }
    }

    weeks.reverse();

    // Use backend aggregate stats in live mode when available
    if (mode === 'live' && summary?.performanceMetrics?.submissionConsistency) {
      const sc = summary.performanceMetrics.submissionConsistency;
      const onTimePercentage = Number(sc.onTimeRateLast12Weeks) * 100;
      const onTimeCount = sc.onTimeCount;
      const lateCount = sc.lateCount;
      const missingCount = sc.missingCount;

      // Calculate trend from the visual timeline (backend doesn't provide trend)
      const recentWeeks = weeks.slice(-5, -1);
      const previousWeeks = weeks.slice(-9, -5);
      const recentOnTime = recentWeeks.filter(w => w.status === 'on-time').length;
      const previousOnTime = previousWeeks.filter(w => w.status === 'on-time').length;
      const recentPercentage = recentWeeks.length > 0 ? (recentOnTime / recentWeeks.length) * 100 : 0;
      const previousPercentage = previousWeeks.length > 0 ? (previousOnTime / previousWeeks.length) * 100 : 0;
      const trend = recentPercentage - previousPercentage;

      let rating: 'excellent' | 'good' | 'needs-improvement' | 'poor';
      if (onTimePercentage >= 90) rating = 'excellent';
      else if (onTimePercentage >= 75) rating = 'good';
      else if (onTimePercentage >= 50) rating = 'needs-improvement';
      else rating = 'poor';

      return {
        weeks,
        onTimeCount,
        lateCount,
        missingCount,
        onTimePercentage,
        trend,
        rating
      };
    }

    // Fallback to client-side calculation for demo mode
    const onTimeCount = weeks.filter(w => w.status === 'on-time').length;
    const lateCount = weeks.filter(w => w.status === 'late').length;
    const missingCount = weeks.filter(w => w.status === 'missing').length;
    const totalWeeks = weeks.filter(w => w.status !== 'current').length;
    
    const onTimePercentage = totalWeeks > 0 ? (onTimeCount / totalWeeks) * 100 : 0;

    const recentWeeks = weeks.slice(-5, -1);
    const previousWeeks = weeks.slice(-9, -5);
    
    const recentOnTime = recentWeeks.filter(w => w.status === 'on-time').length;
    const previousOnTime = previousWeeks.filter(w => w.status === 'on-time').length;
    
    const recentPercentage = recentWeeks.length > 0 ? (recentOnTime / recentWeeks.length) * 100 : 0;
    const previousPercentage = previousWeeks.length > 0 ? (previousOnTime / previousWeeks.length) * 100 : 0;
    const trend = recentPercentage - previousPercentage;

    let rating: 'excellent' | 'good' | 'needs-improvement' | 'poor';
    if (onTimePercentage >= 90) rating = 'excellent';
    else if (onTimePercentage >= 75) rating = 'good';
    else if (onTimePercentage >= 50) rating = 'needs-improvement';
    else rating = 'poor';

    return {
      weeks,
      onTimeCount,
      lateCount,
      missingCount,
      onTimePercentage,
      trend,
      rating
    };
  }, [mode, summary, timesheets, currentEmployee]);

  // Rating configuration
  const ratingConfig = {
    excellent: {
      accentClass: 'accent-success',
      label: 'Excellent',
      icon: CheckCircle
    },
    good: {
      accentClass: 'accent-info',
      label: 'Good',
      icon: CheckCircle
    },
    'needs-improvement': {
      accentClass: 'accent-warning',
      label: 'Needs Work',
      icon: Clock
    },
    poor: {
      accentClass: 'accent-danger',
      label: 'Poor',
      icon: XCircle
    }
  };

  const currentRating = ratingConfig[consistencyData.rating];
  const RatingIcon = currentRating.icon;

  // Handle week click to navigate to timesheet
  const handleWeekClick = (weekEnding: Date) => {
    navigate('/timesheet', {
      state: {
        timesheetDrilldown: {
          employeeId: currentEmployee.id,
          weekEnding: weekEnding
        }
      }
    });
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-4">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold">
            Submission Consistency
          </CardTitle>
        </div>
        <Badge 
          variant="secondary"
          className={`${currentRating.accentClass} bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] border-[var(--accent-border)]`}
        >
          <RatingIcon className="h-3 w-3 mr-1" />
          {currentRating.label}
        </Badge>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Main Percentage */}
        <div className="text-center">
          <div className="text-3xl font-bold">{consistencyData.onTimePercentage.toFixed(0)}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            On-time submissions (last 12 weeks)
          </p>
          {consistencyData.trend !== 0 && (
            <div className="flex items-center justify-center gap-1 mt-2">
              {consistencyData.trend > 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-success-600" />
                  <span className="text-xs text-success-600 font-medium">
                    +{consistencyData.trend.toFixed(0)}% vs previous period
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-error-600" />
                  <span className="text-xs text-error-600 font-medium">
                    {consistencyData.trend.toFixed(0)}% vs previous period
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Visual Timeline */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Last 12 Weeks</p>
          <div className="flex gap-1">
            {consistencyData.weeks.map((week, index) => {
              const isCurrentWeek = week.status === 'current';
              const weekLabel = format(week.weekEnding, 'MMM d');
              
              let bgColor = 'bg-neutral-200';
              let tooltip = 'No data';
              
              if (week.status === 'on-time') {
                bgColor = 'bg-[var(--success-400)]';
                tooltip = `${weekLabel}: On time`;
              } else if (week.status === 'late') {
                bgColor = 'bg-[var(--warning-400)]';
                tooltip = `${weekLabel}: Late (${week.daysLate} days)`;
              } else if (week.status === 'missing') {
                bgColor = 'bg-[var(--error-400)]';
                tooltip = `${weekLabel}: Missing`;
              } else if (week.status === 'current') {
                bgColor = week.submitted ? 'bg-[var(--info-400)]' : 'bg-neutral-300';
                tooltip = `${weekLabel}: Current week`;
              }

              return (
                <div
                  key={index}
                  className="group relative flex-1"
                  title={tooltip}
                >
                  <button
                    onClick={() => handleWeekClick(week.weekEnding)}
                    className={`w-full h-8 rounded ${bgColor} transition-all hover:opacity-80 hover:scale-105 cursor-pointer ${
                      isCurrentWeek ? 'ring-2 ring-info-400 ring-offset-1' : ''
                    }`}
                    aria-label={`View timesheet for week ending ${weekLabel}`}
                  />
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    {tooltip}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-[var(--success-400)]" />
              <span>On time</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-[var(--warning-400)]" />
              <span>Late</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-[var(--error-400)]" />
              <span>Missing</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-[var(--info-400)] ring-2 ring-info-400 ring-offset-1" />
              <span>Current</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t">
          <div className="text-center">
            <div className="text-lg font-bold text-success-600">{consistencyData.onTimeCount}</div>
            <p className="text-xs text-muted-foreground">On Time</p>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-warning-600">{consistencyData.lateCount}</div>
            <p className="text-xs text-muted-foreground">Late</p>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-error-600">{consistencyData.missingCount}</div>
            <p className="text-xs text-muted-foreground">Missing</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
